<script setup>
import { computed, toRaw, watch } from 'vue';
import { useUserStore } from '@/stores/user';
import { useForm } from 'laravel-precognition-vue';

const form = useForm('patch', '/api/v1/profile', {
    email: null,
    first_name: null,
    last_name: null,
    password: null,
    password_confirmation: null,
    navigation: 'side',
});

const userStore = useUserStore();

watch(() => userStore.profile, () => {
    form.setData(toRaw({ ...userStore.profile }));
});

const initials = computed(() => {
    const profile = userStore.profile;
    if (!profile) {
        return '';
    }
    return `${profile.first_name?.[0] ?? ''}${profile.last_name?.[0] ?? ''}`.toUpperCase();
});

const roleLabel = computed(() => userStore.getUserRoleString(userStore.profile) ?? '');

const cancel = () => {
    window.history.go(-1);
};

const save = () => {
    form.submit();
};
</script>

<template>
<MainContentLayout>
    <template #breadcrumb>
        <BreadCrumb />
    </template>
    <template #title>
        {{ $t('label.profile') }}
    </template>
    <template #actions>
        <StyledButton
            size="small"
            variant="outlined"
            @click="$inertia.visit('/profile/security-questions')">
            <FontAwesomeIcon
                icon="lock"
                class="mr-1" />
            {{ $t('label.change_security_questions_and_answers') }}
        </StyledButton>
    </template>

    <div class="max-w-2xl space-y-4">
        <div class="rounded-xl border border-line bg-surface p-5 flex items-center gap-4">
            <div class="w-14 h-14 rounded-full flex items-center justify-center text-lg font-bold text-white bg-secondary shrink-0">
                {{ initials }}
            </div>
            <div class="min-w-0">
                <p class="font-display font-semibold text-lg truncate">{{ userStore.profile?.first_name }} {{ userStore.profile?.last_name }}</p>
                <p class="text-sm text-ink-light truncate">{{ roleLabel }}</p>
            </div>
        </div>

        <form
            class="space-y-4"
            @submit.prevent="save">
            <div class="rounded-xl border border-line bg-surface p-5">
                <h3 class="font-display font-semibold text-base mb-4">{{ $t('label.personal_information') }}</h3>
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <StyledInput
                        v-model="form.first_name"
                        :label="$t('label.first_name')"
                        :placeholder="$t('label.first_name')"
                        name="first_name"
                        @change="form.validate('first_name')" />
                    <StyledInput
                        v-model="form.last_name"
                        :label="$t('label.last_name')"
                        :placeholder="$t('label.last_name')"
                        name="last_name"
                        @change="form.validate('last_name')" />
                    <StyledInput
                        v-model="form.email"
                        class="sm:col-span-2"
                        :label="$t('label.email')"
                        :placeholder="$t('label.email')"
                        name="email"
                        type="email"
                        @change="form.validate('email')" />
                </div>
            </div>

            <div class="rounded-xl border border-line bg-surface p-5">
                <h3 class="font-display font-semibold text-base mb-1">{{ $t('label.password') }}</h3>
                <p class="text-xs text-ink-light mb-4">{{ $t('label.password_change_hint') }}</p>
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <StyledInput
                        v-model="form.password"
                        :label="$t('label.password')"
                        :placeholder="$t('label.password')"
                        name="password"
                        autocomplete="new-password"
                        type="password" />
                    <StyledInput
                        v-model="form.password_confirmation"
                        :label="$t('label.password_confirmation')"
                        :placeholder="$t('label.password_confirmation')"
                        name="password_confirmation"
                        type="password" />
                </div>
            </div>

            <StyledSaveButton
                @save="save"
                @cancel="cancel" />
        </form>
    </div>
</MainContentLayout>
</template>
